import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { isValid, parse } from 'date-fns';
import {
  BehaviorSubject,
  iif,
  merge,
  of,
  pipe,
  Subject,
  withLatestFrom
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';
import { Item, Product } from '../../models';
import { BookingItem } from '../../models/booking-item';
import { ProductCategory } from '../../models/product-category';
import { ProductService } from '../../shared';
import { BookingItemService } from '../../shared/services/booking-item.service';
import { ProductCategoryService } from '../../shared/services/product-category.service';

interface EditBookingItemState {
  bookingId: number;
  saving: boolean;
  loadingItems: boolean;
  loadingProducts: boolean;
  loadingCategories: boolean;
  bookingItemId: number | null;
  categoryId: number;
  productId: number | null;
  itemId: number | null;
  categories: ProductCategory[];
  productCategoryCache: { [categoryId: number]: Product[] };
  productItemsCache: { [productId: number]: Item[] };
  onHire: string;
  offHire: string;
  quantity: number;
  dirty: boolean;
}

const initialState: EditBookingItemState = {
  loadingCategories: true,
  bookingId: null,
  saving: false,
  loadingItems: false,
  loadingProducts: false,
  bookingItemId: null,
  categoryId: null,
  productId: null,
  itemId: null,
  categories: [],
  productCategoryCache: {},
  productItemsCache: {},
  onHire: null,
  offHire: null,
  quantity: null,
  dirty: true
};

@Injectable()
export class EditBookingItemStoreService extends ComponentStore<EditBookingItemState> {
  constructor(
    private productService: ProductService,
    private categoryService: ProductCategoryService,
    private bookingItemService: BookingItemService
  ) {
    super(initialState);
    this.loadCategories();
  }

  readonly clearProductField = this.select(
    ({ loadingProducts }) => loadingProducts
  ).pipe(
    // only proceed pipeline if loading is true
    filter((loadingProducts) => loadingProducts),
    // wait for loading to be false
    mergeMap(() => this.select(({ loadingProducts }) => loadingProducts)),
    filter((loadingProducts) => !loadingProducts),
    // then check if product id is set and is in the current list of products
    mergeMap(() =>
      this.select((state) => state.productId).pipe(
        withLatestFrom(this.products$)
      )
    ),
    filter(
      ([productId, products]) =>
        productId && !products.find((p) => p.id === productId)
    ),
    // if not, clear the product id
    map(() => null)
  );

  readonly clearItemField = this.select(
    ({ loadingItems }) => loadingItems
  ).pipe(
    // only proceed pipeline if loading is true
    filter((loadingItems) => loadingItems),
    // wait for loading to be false
    mergeMap(() => this.select(({ loadingItems }) => loadingItems)),
    filter((loadingItems) => !loadingItems),
    // then check if item id is set and is in the current list of items
    mergeMap(() =>
      this.select((state) => state.itemId).pipe(withLatestFrom(this.items$))
    ),
    filter(([itemId, items]) => itemId && !items.find((p) => p.id === itemId)),
    // if not, clear the item id
    map(() => null)
  );

  readonly loading$ = this.select(
    ({ loadingCategories, loadingProducts, loadingItems }) =>
      loadingCategories || loadingProducts || loadingItems
  ).pipe(debounceTime(50));

  readonly categories$ = this.select((state) => state.categories).pipe(
    map((categories) => {
      return categories
        .map((category) => {
          return { id: category.id, name: category.name };
        })
        .concat([{ id: -1, name: 'Uncategorised' }]);
    })
  );
  readonly products$ = this.select(
    ({ categoryId, productCategoryCache }) => categoryId
  ).pipe(
    distinctUntilChanged(),
    mergeMap((categoryId) =>
      this.select((state) => state.productCategoryCache).pipe(
        map((productCategoryCache) => ({
          categoryId,
          productCategoryCache
        }))
      )
    ),
    map(({ categoryId, productCategoryCache }) => {
      if (!categoryId) {
        return [];
      }
      if (productCategoryCache[categoryId]) {
        return productCategoryCache[categoryId];
      }
      this.loadProductsForCategory(categoryId);
      return [];
    })
  );

  readonly isVirtualProduct$ = this.select(
    ({ categoryId, productId, productCategoryCache }) => {
      if (!productId) {
        return false;
      }
      const product = productCategoryCache[categoryId]?.find(
        (p) => p.id === productId
      );
      return product?.virtual ?? false;
    }
  );

  readonly items$ = this.select(
    ({ productId, productItemsCache }) => productId
  ).pipe(
    distinctUntilChanged(),
    mergeMap((productId) =>
      this.select((state) => state.productItemsCache).pipe(
        map((productItemsCache) => ({
          productId,
          productItemsCache
        }))
      )
    ),
    map(({ productId, productItemsCache }) => {
      if (!productId) {
        return [];
      }
      if (productItemsCache[productId]) {
        return productItemsCache[productId];
      }
      this.loadItemsForProduct(productId);
      return [];
    })
  );

  readonly availableCount$ = this.select((state) => {
    if (state.itemId) {
      return {
        product_id: state.productId,
        category_id: state.categoryId,
        item_id: state.itemId,
        bookingItemId: state.bookingItemId
      };
    }
    if (state.productId) {
      return {
        category_id: state.categoryId,
        product_id: state.productId,
        bookingItemId: state.bookingItemId
      };
    }
    if (state.categoryId) {
      return {
        category_id: state.categoryId,
        bookingItemId: state.bookingItemId
      };
    }
    return null;
  }).pipe(
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    filter((value) => !!value),
    withLatestFrom(
      this.select((state) => state.onHire),
      this.select((state) => state.offHire)
    ),
    switchMap(([payload, onHire, offHire]) => {
      return this.bookingItemService.getProspectiveAvailability({
        ...payload,
        on_hire: onHire,
        off_hire: offHire
      });
    })
  );

  readonly isClean$ = this.select((state) => !state.dirty);

  readonly setInitialBookingItem = this.updater(
    (state, bookingItem: Partial<BookingItem>) => {
      return {
        ...state,
        bookingId: bookingItem.booking_id,
        bookingItemId: bookingItem.id,
        categoryId: bookingItem.category_id,
        productId: bookingItem.product_id,
        itemId: bookingItem.item_id,
        onHire: bookingItem.on_hire?.toString(),
        offHire: bookingItem.off_hire?.toString()
      };
    }
  );

  private readonly suggestedPricePayload$ = this.select((state) => {
    if (state.productId) {
      return {
        product_id: state.productId,
        on_hire: state.onHire,
        off_hire: state.offHire,
        hireable_type: 'bookingItem',
        hireable_id: state.bookingItemId,
        quantity: state.quantity ?? 1
      };
    }
    return null;
  });

  readonly triggerSuggestedPriceUpdate = new BehaviorSubject<void>(null);

  readonly suggestedPrice$ = merge(
    this.suggestedPricePayload$,
    // if the trigger is emitted, update the suggested price
    this.triggerSuggestedPriceUpdate
      .asObservable()
      .pipe(mergeMap(() => this.suggestedPricePayload$))
  ).pipe(
    debounceTime(200),
    switchMap((payload) => {
      return iif(
        () => !!payload,
        this.productService
          .getPrice([payload])
          .pipe(map((prices) => prices[0])),
        of(null)
      );
    })
  );

  private readonly loadCategories = this.effect<void>(
    pipe(
      exhaustMap(() => this.categoryService.getProductCategories()),
      tapResponse(
        (categories: ProductCategory[]) =>
          this.patchState((state) => ({
            ...state,
            categories,
            // if product id set, don't set loading to false yet
            loadingCategories: false
          })),
        (error) => console.error(error)
      )
    )
  );

  private loadProductsForCategory = this.effect<number>(
    pipe(
      tap(() => this.patchState({ loadingProducts: true })),
      switchMap((categoryId) =>
        iif(
          () => categoryId === -1,
          this.productService.getUncategorised(),
          this.categoryService
            .getProductCategory(categoryId, ['products'])
            .pipe(map(({ products }) => products))
        ).pipe(
          tapResponse(
            (products) =>
              this.patchState((state) => ({
                ...state,
                productCategoryCache: {
                  ...state.productCategoryCache,
                  [categoryId]: products
                },
                // if item id set, don't set loading to false yet
                loadingProducts: false
              })),
            (error) => console.error(error)
          )
        )
      )
    )
  );

  private loadItemsForProduct = this.effect<number>(
    pipe(
      tap(() => this.patchState({ loadingItems: true })),
      switchMap((productId) =>
        this.productService.getProduct(productId, ['items']).pipe(
          map(({ items }) => items.filter((item) => item.deleted === 0)),
          tapResponse(
            (items) =>
              this.patchState((state) => ({
                ...state,
                productItemsCache: {
                  ...state.productItemsCache,
                  [productId]: items
                },
                loadingItems: false
              })),
            (error) => console.error(error)
          )
        )
      )
    )
  );

  readonly priceStructureParams$ = this.select((state) => {
    return {
      product_id: state.productId,
      on_hire: state.onHire,
      off_hire: state.offHire,
      hireable_type: 'bookingItem',
      hireable_id: state.bookingItemId,
      quantity: state.quantity ?? 1,
      booking_id: state.bookingId
    };
  });

  readonly saveBookingItem = this.effect<{
    bookingItem: Partial<BookingItem>;
    onSuccess: () => void;
  }>(
    pipe(
      tap(() => this.patchState({ saving: true })),
      withLatestFrom(
        this.select(({ bookingItemId, bookingId }) => ({
          bookingItemId,
          bookingId
        }))
      ),
      switchMap(
        ([{ bookingItem, onSuccess }, { bookingItemId, bookingId }]) => {
          return iif(
            () => !!bookingItemId,
            this.bookingItemService.update(
              bookingItemId,
              bookingId,
              bookingItem
            ),
            this.bookingItemService.create(bookingId, bookingItem).pipe(
              tap((bookingItem) => {
                this.patchState({
                  bookingItemId: bookingItem.id
                });
              })
            )
          ).pipe(
            tapResponse(
              (bookingItem) => {
                this.patchState({
                  saving: false,
                  dirty: false
                });
                onSuccess();
              },
              (error) => console.error(error)
            )
          );
        }
      )
    )
  );

  listenForFormChanges(form: FormGroup) {
    const {
      on_hire,
      on_hire_time,
      off_hire,
      off_hire_time,
      category_id,
      product_id,
      item_id,
      quantity
    } = form.controls;
    merge(
      on_hire.valueChanges,
      on_hire_time.valueChanges,
      off_hire_time.valueChanges,
      off_hire.valueChanges,
      category_id.valueChanges,
      product_id.valueChanges,
      item_id.valueChanges,
      quantity.valueChanges
    )
      .pipe(
        debounceTime(50),
        tap(() => this.patchState({ dirty: true })),
        map(() => this.mapFormToState(form.value)),
        tap((state) => this.patchState(state)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private mapFormToState(value: any) {
    const onHire =
      value.on_hire && value.on_hire_time
        ? parse(value.on_hire_time, 'HH:mm', value.on_hire.toDate())
        : null;
    const offHire =
      value.off_hire && value.off_hire_time
        ? parse(value.off_hire_time, 'HH:mm', value.off_hire.toDate())
        : null;

    const state: Partial<EditBookingItemState> = {
      categoryId: value.category_id,
      productId: value.product_id,
      itemId: value.item_id,
      offHire: isValid(offHire) ? offHire.toISOString() : null,
      onHire: isValid(onHire) ? onHire.toISOString() : null,
      quantity: value.quantity
    };
    return state;
  }
}
